import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LoggingService } from '@services/logging.service';
import { AuthConfig, OAuthService, NullValidationHandler } from 'angular-oauth2-oidc';
import { filter } from 'rxjs/operators';
import { MonitoringService } from './monitoring.service';
import { ConfigService } from './config.service';
import * as Constants from '@shared/_constants';
import { ActiveComponentService, DeactivationStatus } from '@services/active-component.service';
import { ServiceNowService } from '@services/servicenow.service';
import { firstValueFrom, Observable } from 'rxjs';
import { globalCacheBusterNotifier } from 'ts-cacheable';

/*
    Service responsible for configuring Azure AD auth and logging in/out and managing Azure AD Tokens
    OAuthService - https://github.com/manfredsteyer/angular-oauth2-oidc
    Azure AD Claims - https://docs.microsoft.com/en-us/azure/active-directory/develop/active-directory-claims-mapping
*/
@Injectable()
export class AuthService {
  private isConfigured = false;
  private azure_claim_upn: string;
  public tokenReceived: EventEmitter<boolean> = new EventEmitter();
  public tokenExpired: EventEmitter<boolean> = new EventEmitter();
  public configured: EventEmitter<boolean> = new EventEmitter();

  constructor(
    private http: HttpClient,
    private oauthService: OAuthService,
    private monitoringService: MonitoringService,
    private configService: ConfigService,
    private logger: LoggingService,
    private activeComponentService: ActiveComponentService,
    private serviceNowService: ServiceNowService,
  ) {
    this.azure_claim_upn = undefined;
  }

  public async setupConfig(): Promise<void> {
    await this.configureOAuthService();
  }

  public async logIn() {
    this.logger.info(this.constructor.name, 'logIn', `Start [isConfigured:${this.isConfigured}]`);
    // IE Edge doesn't keep our object during implicit flow so this check is just for you.  I hate you IE Edge why can't you be like Google Chrome
    if (this.isConfigured === false) {
      await this.configureOAuthService();
    }
    await this.oauthService.tryLogin({});

    if (!this.oauthService.hasValidAccessToken()) {
      this.logger.info(this.constructor.name, 'logIn', 'Initiate Implicit Flow');
      await this.oauthService.initImplicitFlow();
    } else {
      this.logger.info(
        this.constructor.name,
        'logIn',
        `Token already in OAuthService. Expires: ${new Date(this.oauthService.getAccessTokenExpiration())}`,
      );
    }
  }

  // check for unsaved changes in components with canDeactivate Method
  public async logoutSafetyCheck(): Promise<DeactivationStatus> {
    return this.activeComponentService.canDeactivate(true);
  }

  public async logOut(logoutReasonCode?: Constants.LogoutReasonCode) {
    this.logger.info(this.constructor.name, 'logOut', 'Start');

    // Terminate ServiceNow Engagement Messenger user session on logout
    this.serviceNowService.onLogout();

    globalCacheBusterNotifier.next();
    this.monitoringService.clearAuthenticatedUserContext();
    this.blacklistToken().subscribe();

    if (logoutReasonCode !== undefined && logoutReasonCode !== null) {
      this.oauthService.postLogoutRedirectUri = window.location.origin + `/loggedout?r=${logoutReasonCode}`;
    } else {
      this.oauthService.postLogoutRedirectUri = window.location.origin + '/loggedout';
    }

    sessionStorage.removeItem('screenVersionDisplayed');
    this.oauthService.logOut();
  }

  public blacklistToken(): Observable<any> {
    return this.http.get('api/Auth/BlacklistToken');
  }

  public hasValidAzureADAccessToken(): boolean {
    const validToken = this.oauthService.hasValidAccessToken();
    this.logger.info(this.constructor.name, 'hasValidAzureADAccessToken', `Token valid: ${validToken}`);
    return validToken;
  }

  public async getAzureADAccessToken(): Promise<string> {
    if (this.oauthService.hasValidAccessToken()) {
      this.logger.info(this.constructor.name, 'getAzureADAccessToken', `Valid - Expires: ${new Date(this.oauthService.getAccessTokenExpiration())}`);
      return this.oauthService.getAccessToken();
    } else {
      this.logger.info(this.constructor.name, 'getAzureADAccessToken', 'Invalid - Initiate Implicit Flow');
      await this.oauthService.initImplicitFlow();
      // TODO - test this as it may return too soon before the implicit flow is complete
      return this.oauthService.getAccessToken();
    }
  }

  private async configureOAuthService(): Promise<void> {
    this.logger.info(this.constructor.name, 'configureOAuthServices', 'Start');
    try {
      const results = await firstValueFrom(this.configService.getConfigValues([ConfigService.AZURE_TENANT_ID, ConfigService.AZURE_CLIENT_ID]));

      const azureTenantId = results[ConfigService.AZURE_TENANT_ID];
      const azureClientId = results[ConfigService.AZURE_CLIENT_ID];

      if (azureClientId && azureClientId) {
        const config = this.getAuthConfig(azureTenantId, azureClientId);

        this.oauthService.configure(config);
        this.oauthService.tokenValidationHandler = new NullValidationHandler();
        this.configured.emit(true);
        this.isConfigured = true;

        // Optional - we aren't doing this because we need to know the user's email when we refresh
        // this.oauthService.setupAutomaticSilentRefresh();

        this.oauthService.events.subscribe((e) => {
          this.logger.info(this.constructor.name, 'configureOAuthService', 'oauth/oidc event', e);
        });

        this.oauthService.events.pipe(filter((e) => e.type === 'session_terminated')).subscribe(() => {
          this.logger.info(this.constructor.name, 'configureOAuthService', 'session_terminated');
        });

        this.oauthService.events.pipe(filter((e) => e.type === 'token_expires')).subscribe(() => {
          this.logger.info(this.constructor.name, 'configureOAuthService', 'token_expires');
          this.tokenExpired.emit(true);
          /* 
             Rather than automatic refresh, we will own the refresh so we can pass the user's email, we have to pass the email
             because we get an "interaction_required" OAuth error from Azure AD otherwise
             https://docs.microsoft.com/en-us/azure/active-directory/develop/v1-protocols-oauth-code
            */
          // const claimUPN = this.getClaimUPN();
          // this.oauthService
          //   .silentRefresh({ login_hint: claimUPN })
          //   .then((info) => this.logger.info(this.constructor.name, 'configureOAuthService', 'Silent Refresh ok', info))
          //   .catch((err) => this.logger.error(this.constructor.name, 'configureOAuthService', 'Silent Refresh failed', err));
        });

        this.oauthService.events.pipe(filter((e) => e.type === 'token_received')).subscribe(() => {
          // this.oauthService.loadUserProfile();
          this.logger.info(this.constructor.name, 'configureOAuthService', 'token_received');
          const upn = this.getClaimUPN();
          this.monitoringService.setAuthenticatedUserContext(upn);
          this.tokenReceived.emit(true);
        });
      }
    } catch (e) {
      this.logger.error(this.constructor.name, 'configureOAuthServices', JSON.stringify(e));
    } finally {
      this.logger.info(this.constructor.name, 'configureOAuthServices', 'End');
    }
  }

  public getClaimUPN(): string {
    this.logger.info(this.constructor.name, 'getClaimUPN', 'Start');
    try {
      if (this.oauthService.hasValidAccessToken()) {
        if (!this.azure_claim_upn) {
          this.azure_claim_upn = this.tryGetIdentityClaim('upn');
        }
      }
      this.logger.info(this.constructor.name, 'getClaimUPN', `[${this.azure_claim_upn}]`);
      return this.azure_claim_upn;
    } finally {
      this.logger.info(this.constructor.name, 'getClaimUPN', 'End');
    }
  }

  private tryGetIdentityClaim(claimName: string) {
    this.logger.info(this.constructor.name, 'tryGetIdentityClaim', 'Start');
    try {
      const claims = this.oauthService.getIdentityClaims();
      if (claims && claims[claimName]) {
        return claims[claimName];
      }
      return undefined;
    } finally {
      this.logger.info(this.constructor.name, 'tryGetIdentityClaim', 'End');
    }
  }

  private getAuthConfig(azureTenantId: string, azureClientId: string): AuthConfig {
    return {
      // Url of the Identity Provider
      issuer: `https://sts.windows.net/${azureTenantId}/`,
      // In Azure - the OAUTH 2.0 AUTHORIZATION ENDPOINT
      loginUrl: `https://login.microsoftonline.com/${azureTenantId}/oauth2/authorize`,
      // In Azure - the Application ID for the Registered App
      clientId: azureClientId,
      // In Azure - the url for the resource we need to access
      resource: 'https://graph.microsoft.com',
      // URL of the SPA to redirect the user to after logIn
      redirectUri: window.location.origin + '/',
      // URL of the SPA to redirect the user after silent refresh. We are not silently refreshing token from azure AD.
      // silentRefreshRedirectUri: window.location.origin + '/silent-refresh.html',
      // Percentage of time of access token's lifetime before we silently refresh (e.g. .75 means refresh at 75% of it's lifetime
      // timeoutFactor: 0.01,
      // URL of the SPA to redirect the user to after logOut
      // If we decide to use this we'll need to create a component, handle the route and prevent attempting to logIn in app component based on route
      postLogoutRedirectUri: window.location.origin + '/loggedout',
      // In Azure - the OAUTH 2.0 AUTHORIZATION ENDPOINT
      logoutUrl: `https://login.microsoftonline.com/${azureTenantId}/oauth2/logout`,
      // Permissions to be requested by the client
      scope: 'openid User.Read User.ReadBasic.All',
      // Use OpenId Connect during implicit flow
      oidc: true,
      // Remote Only means localhost doesn't need https, but everything else does
      requireHttps: 'remoteOnly',
      // Defines whether to clear the hash fragment after logging in. (Set to true because we don't want the token in the url after signin)
      clearHashAfterLogin: true,
      // TODO - determine if we need/want to do this https://openid.net/specs/openid-connect-session-1_0.html#ChangeNotification
      // sessionChecksEnabled
      // Turn on for testing
      showDebugInformation: true,
      silentRefreshTimeout: ConfigService.SILENT_REFRESH_TIMEOUT,
      //Default silentRefreshTimeout is 20000. need to set to handel "silent_refresh_timeout" issue
    };
  }
}
